import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Wallet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function PaymentMethodReport({ token }) {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payments/by-method${month ? `?month=${month}` : ""}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setData(r.data); if (!month) setMonth(r.data.month); })
      .catch(() => toast.error("Could not load the payment method report."));
  }, [token, month]);

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  const max = Math.max(...data.items.map((i) => i.total), 1);

  return (
    <div className="space-y-4" data-testid="payment-method-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-display font-bold text-lg flex items-center gap-2"><Wallet size={18} className="text-[#F2B705]" /> Revenue by Payment Method</div>
        <select data-testid="pm-month-select" value={month} onChange={(e) => setMonth(e.target.value)}
          className="bg-black border border-white/15 rounded px-3 py-1.5 text-xs text-white">
          {(data.months.length ? data.months : [data.month]).map((m) => <option key={m} value={m} className="bg-black">{m}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Collected in {data.month}</div>
          <div data-testid="pm-total" className="font-display font-bold text-2xl text-emerald-400 mt-1">{money(data.total)}</div>
        </div>
        <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Payments recorded</div>
          <div className="font-display font-bold text-2xl mt-1">{data.payments}</div>
        </div>
      </div>
      {data.items.length === 0 ? (
        <p className="text-white/40 text-sm border border-white/10 rounded-lg bg-[#0A0A0A] p-6 text-center">No payments recorded in {data.month}.</p>
      ) : (
        <div className="space-y-2">
          {data.items.map((i) => (
            <div key={i.method} data-testid={`pm-row-${i.method}`} className="bg-[#0C0C0C] border border-white/10 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold">{i.label} <span className="text-white/35 text-xs">· {i.count} payment{i.count !== 1 ? "s" : ""}</span></span>
                <span className="font-display font-bold text-[#F2B705] tabular-nums">{money(i.total)}
                  {i.refunded > 0 && <span className="text-red-400 text-xs font-normal ml-2">−{money(i.refunded)} refunded</span>}
                </span>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-[#F2B705] rounded-full transition-all" style={{ width: `${Math.max(4, (i.total / max) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-white/35 text-[11px]">Every payment recorded through Financial Actions, Square Sync, Stripe or extensions lands here by its method — so you see how money actually comes in.</p>
    </div>
  );
}
